import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdminAuthContextType {
  isAdmin: boolean;
  currentUsername: string;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => boolean;
  changeUsername: (currentPassword: string, newUsername: string) => boolean;
  showLoginModal: boolean;
  setShowLoginModal: (v: boolean) => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

const DEFAULT_USERNAME = 'Mapping';
const DEFAULT_PASSWORD = '7878';
const PASSWORD_STORAGE_KEY = 'geoeng_admin_pw';
const USERNAME_STORAGE_KEY = 'geoeng_admin_user';

function getStoredPassword(): string {
  return localStorage.getItem(PASSWORD_STORAGE_KEY) ?? DEFAULT_PASSWORD;
}

function getStoredUsername(): string {
  return localStorage.getItem(USERNAME_STORAGE_KEY) ?? DEFAULT_USERNAME;
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentUsername, setCurrentUsername] = useState<string>(getStoredUsername);

  useEffect(() => {
    const stored = sessionStorage.getItem('geoeng_admin');
    if (stored === 'true') setIsAdmin(true);
  }, []);

  const login = (username: string, password: string): boolean => {
    if (username === getStoredUsername() && password === getStoredPassword()) {
      setIsAdmin(true);
      sessionStorage.setItem('geoeng_admin', 'true');
      setShowLoginModal(false);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('geoeng_admin');
  };

  const changePassword = (currentPassword: string, newPassword: string): boolean => {
    if (currentPassword !== getStoredPassword()) return false;
    localStorage.setItem(PASSWORD_STORAGE_KEY, newPassword);
    return true;
  };

  const changeUsername = (currentPassword: string, newUsername: string): boolean => {
    if (currentPassword !== getStoredPassword()) return false;
    localStorage.setItem(USERNAME_STORAGE_KEY, newUsername);
    setCurrentUsername(newUsername);
    return true;
  };

  return (
    <AdminAuthContext.Provider value={{ isAdmin, currentUsername, login, logout, changePassword, changeUsername, showLoginModal, setShowLoginModal }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
